import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import GridTable from "pages/_components/GridTable/GridTable";
import I18n from "pages/_components/I18n";
import Button from "pages/_components/Button";
import FormattedAmount from "pages/_components/FormattedAmount";
import CustomData from "pages/_components/CustomData/CustomData";
import Loader from "pages/_components/Loader";
import SummaryActions from "pages/echeck/components/SummaryActions";
import { findEmisor } from "util/echecks";
import { useGetAccounts } from "hooks/useAccounts";
import * as i18n from "util/i18n";
import { types } from "pages/echeck/constants";
import { actions as notificationActions } from "reducers/notification";
import { useExecuteAction } from "../hooks/useExecuteAction";

const ConfirmEcheckPage = () => {
    const { environmentCuit, echeqsToDoActions: echeqAction } = useSelector((state) => state.echecks);
    const { isSubmitting, executeEcheqAction } = useExecuteAction(echeqAction);
    const { accounts } = useGetAccounts();

    const dispatch = useDispatch();

    const formatAccountLabel = (account) =>
        `${i18n.get(`accounts.productType.${types[account.accountType]}`)} ····${account.accountNumber?.slice(-4)} ${
            account.accountType
        } ${account.currency}`;

    const createAccountDescription = () => {
        const { specialAttribute } = echeqAction;
        const account = accounts.find((a) => a.productId === specialAttribute);

        return formatAccountLabel(account);
    };

    useEffect(() => {
        for (const echeq of echeqAction?.echeqList) {
            const { moneda } = echeq;
            const enabledAccounts = accounts.filter((account) => account.currency === moneda);

            const currency = { CURRENCY: i18n.get(`echeq.currency.${moneda}`) };

            // Mostramos snackbar solo para admitir echeq
            if (echeqAction.action == "ADMIT" && enabledAccounts && enabledAccounts.length == 0) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("echeq.noAccountSameCurrency", null, currency),
                        "warning",
                        ["menu"],
                    ),
                );
            }
        }
    }, []);

    if (isEmpty(echeqAction)) {
        return <Loader />;
    }

    return (
        <>
            <ResponsiveHeader id={`echecks.${echeqAction?.action?.toLowerCase()}`} />
            <MainContainer>
                {echeqAction?.echeqList?.length > 1 && <SummaryActions list={echeqAction?.echeqList} currency="ARS" />}
                <div className="container">
                    <I18n id="echecks.summary.confirm" component="h2" />
                    {echeqAction?.echeqList?.map((echeq) => {
                        const emisor = findEmisor(echeq, environmentCuit);

                        return (
                            <>
                                <GridTable className="form-summary">
                                    <GridTable.Container columns={3} rows={4}>
                                        <GridTable.Data columnStart={1} rowStart={1}>
                                            <CustomData
                                                titleText="echecks.number.label"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_numero}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} rowStart={1}>
                                            <CustomData
                                                titleText="echecks.emisor"
                                                titleTextType="I18N"
                                                valueText={emisor.emisorRazonSocial}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={3} rowStart={1}>
                                            <CustomData
                                                titleText="echecks.document"
                                                titleTextType="I18N"
                                                valueText={emisor.emisorDocumento}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={1} rowStart={2}>
                                            <CustomData
                                                titleText="echecks.amount"
                                                titleTextType="I18N"
                                                valueText={
                                                    <FormattedAmount
                                                        className="data-label"
                                                        currency={echeq.emisor_moneda}
                                                        quantity={echeq.monto}
                                                    />
                                                }
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} rowStart={2}>
                                            <CustomData
                                                titleText="echecks.id.label"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_id}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={3} rowStart={2}>
                                            <CustomData
                                                titleText="echecks.emit.type.label"
                                                titleTextType="I18N"
                                                valueText={echeq?.cheque_tipo}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={1} rowStart={3}>
                                            <CustomData
                                                titleText="echecks.currency"
                                                titleTextType="I18N"
                                                valueText={echeq.moneda}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} rowStart={3}>
                                            <CustomData
                                                titleText="echecks.echeckNature"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_caracter}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={3} rowStart={3}>
                                            <CustomData
                                                titleText="echecks.payedRange.label"
                                                titleTextType="I18N"
                                                valueText={echeq.fecha_pago}
                                                valueTextType="DATE"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={1} rowStart={4}>
                                            <CustomData
                                                titleText="echecks.echeckConcept"
                                                titleTextType="I18N"
                                                valueText={`transfer.reasons.${echeq.cheque_concepto}`}
                                                valueTextType="I18N"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} rowStart={4}>
                                            <CustomData
                                                titleText="echecks.emit.motive.label"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_motivo_pago}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={1} rowStart={5}>
                                            <CustomData
                                                titleText="echecks.echeckMode"
                                                titleTextType="I18N"
                                                valueText={
                                                    echeq.cheque_modo === 1
                                                        ? "echecks.emit.cross"
                                                        : "echecks.emit.uncross"
                                                }
                                                valueTextType="I18N"
                                            />
                                        </GridTable.Data>
                                        {(echeqAction.action === "REPUDIATE" ||
                                            echeqAction.action === "REPUDIATE-CESION") && (
                                            <GridTable.Data columnStart={3} rowStart={4}>
                                                <CustomData
                                                    titleText="echeck.repudiate.label"
                                                    titleTextType="I18N"
                                                    valueText={echeqAction.specialAttribute}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}

                                        {echeqAction.action === "DEPOSIT" && (
                                            <GridTable.Data columnStart={3} rowStart={4}>
                                                <CustomData
                                                    titleText="echeck.deposit.label"
                                                    titleTextType="I18N"
                                                    valueText={createAccountDescription()}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}
                                        {echeqAction.action === "CUSTODY" && (
                                            <GridTable.Data columnStart={3} rowStart={4}>
                                                <CustomData
                                                    titleText="echeck.custody.label"
                                                    titleTextType="I18N"
                                                    valueText={createAccountDescription()}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}
                                        {echeqAction.action === "REQUEST-RETURN" && (
                                            <GridTable.Data columnStart={3} rowStart={4}>
                                                <CustomData
                                                    titleText="echeck.custody.label"
                                                    titleTextType="I18N"
                                                    valueText={createAccountDescription()}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}
                                    </GridTable.Container>
                                </GridTable>
                            </>
                        );
                    })}
                    <GridTable>
                        <GridTable.Container columns={12} columnsTemplate="fixed" className="form-actions">
                            <GridTable.Data columnStart={5} columnWidth={4}>
                                <div>
                                    {echeqAction.action === "RESCUE" && (
                                        <CustomData
                                            icon="images/information.svg"
                                            titleText="echeck.rescue.hint"
                                            titleTextType="I18N"
                                        />
                                    )}

                                    {echeqAction.action === "DEPOSIT" && (
                                        <CustomData
                                            icon="images/information.svg"
                                            titleText="echeck.deposit.hint"
                                            titleTextType="I18N"
                                        />
                                    )}
                                    {echeqAction.action === "CUSTODY" && (
                                        <CustomData
                                            icon="images/information.svg"
                                            titleText="echeck.custody.hint"
                                            titleTextType="I18N"
                                        />
                                    )}
                                </div>
                            </GridTable.Data>
                            <GridTable.Data columnStart={5} columnWidth={4}>
                                <Button
                                    type="submit"
                                    bsStyle="primary"
                                    label="global.accept"
                                    onClick={executeEcheqAction}
                                    loading={isSubmitting}
                                />
                            </GridTable.Data>
                        </GridTable.Container>
                    </GridTable>
                </div>
            </MainContainer>
        </>
    );
};

export default ConfirmEcheckPage;
